import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
// The embed-URL math lives in utils/lofiEmbed.js so it is unit-testable —
// the URL is the whole track-advance mechanism (no track-end detection here),
// and it shipped the 08-15 repeat bug with zero coverage while inlined.
import { parseYouTubeUrl, buildEmbedUrl } from '../utils/lofiEmbed';
import { buildRailEntries, formatTrackDuration, resolveDeckPosition, rebaseDeckToHead } from '../utils/musicRail';
const STORAGE_KEY = 'lofi_radio_active';
const CUSTOM_URL_KEY = 'lofi_radio_custom_url';

// Has the user interacted with the page this load? Any click/tap/key unlocks
// audible autoplay — and the common path into a space IS a click (explore
// tile, rail card), so builder music can start immediately on arrival.
// Hard loads / pasted URLs stay locked until the first interaction.
let audioGestureSeen = false;
if (typeof document !== 'undefined') {
  const markGesture = () => { audioGestureSeen = true; };
  document.addEventListener('pointerdown', markGesture, { capture: true, once: true });
  document.addEventListener('keydown', markGesture, { capture: true, once: true });
}

const LofiRadio = () => {
  const location = useLocation();
  const isGamePage = location.pathname === '/game-step';

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // radioActive = iframe exists, music is playing
  // The DEFAULT lofi stream never auto-starts (♫ button only). Builder space
  // music (spaceMusicUrl) DOES auto-start after a user gesture — see the D6
  // auto-start effect below. (Previous comment here claimed music never
  // auto-starts; stale since D6, corrected 08-01.)
  const [radioActive, setRadioActive] = useState(false);

  // showPlayer = dropdown is visible (can be minimized while music keeps playing)
  const [showPlayer, setShowPlayer] = useState(false);

  // Custom music URL from the current space (received via game event bus)
  const [spaceMusicUrl, setSpaceMusicUrl] = useState(null);
  // 🎵 J1: the house's music queue ({url, title, by} entries) — played as a
  // looping playlist behind spaceMusicUrl. Null everywhere but the house.
  const [spaceMusicQueue, setSpaceMusicQueue] = useState(null);
  // 🎶 /jukebox shared clock (Nina 08-13): { started_at, head_duration_s }.
  // With full durations (per-entry duration_s on the queue), the room's
  // "needle position" is computed locally at freeze moments — see the deck
  // section below. Null = no sync (house aux decks, builder-only music).
  const [spaceJukebox, setSpaceJukebox] = useState(null);
  // 🎶 Up-next rail data (08-18): the head track's real title (status
  // music_url_title, music_query backfill for houses) and, in a house, who
  // holds the aux right now. Null anywhere the status doesn't say.
  const [spaceHeadTitle, setSpaceHeadTitle] = useState(null);
  const [spaceAuxHolder, setSpaceAuxHolder] = useState(null);
  // Competitive spaces can't run /commands, so the short-deck tip below must
  // never teach one there. Rides the same music event as everything else.
  const [spaceCompetitive, setSpaceCompetitive] = useState(false);
  // Requested volume for SPACE music (0-100, null = player default). Noir
  // scene jazz sends ~25 so the ambience beds stay audible under it. A user's
  // personal override track always plays at full volume.
  const [spaceMusicVolume, setSpaceMusicVolume] = useState(null);
  // User's personal override URL (persisted in localStorage)
  const [userMusicUrl, setUserMusicUrl] = useState(() => {
    try {
      return localStorage.getItem(CUSTOM_URL_KEY) || null;
    } catch {
      return null;
    }
  });
  // Edit mode state
  const [showEdit, setShowEdit] = useState(false);
  const [editInput, setEditInput] = useState('');

  const prevEmbedUrlRef = useRef(null);
  const playStartedAtRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, radioActive ? 'true' : 'false');
    } catch {
      // Silent fail
    }
  }, [radioActive]);

  // Persist user's custom URL to localStorage
  useEffect(() => {
    try {
      if (userMusicUrl) {
        localStorage.setItem(CUSTOM_URL_KEY, userMusicUrl);
      } else {
        localStorage.removeItem(CUSTOM_URL_KEY);
      }
    } catch {
      // Silent fail
    }
  }, [userMusicUrl]);

  // Listen for custom music URL events from GameStepPage
  // Reset user override so the space's own music takes priority
  useEffect(() => {
    const handler = (e) => {
      const url = e.detail?.musicUrl || null;
      setSpaceMusicUrl(url);
      setSpaceMusicQueue(url && Array.isArray(e.detail?.queue) && e.detail.queue.length ? e.detail.queue : null);
      setSpaceJukebox(url ? (e.detail?.jukebox || null) : null);
      setSpaceHeadTitle(url ? (e.detail?.headTitle || null) : null);
      setSpaceAuxHolder(url ? (e.detail?.auxHolder || null) : null);
      setSpaceCompetitive(url ? !!e.detail?.competitive : false);
      setSpaceMusicVolume(typeof e.detail?.volume === 'number' ? e.detail.volume : null);
      if (url) {
        setUserMusicUrl(null);
      }
    };
    document.addEventListener('game:music_url_changed', handler);
    return () => document.removeEventListener('game:music_url_changed', handler);
  }, []);

  // 📺 TV wins the room: while a watch party plays (GameStepPage emits the
  // event), the radio iframe unmounts so the two audio layers never stack.
  // radioActive (the user's preference, localStorage-sticky) is untouched —
  // the iframe remounts and music resumes when the party ends.
  const [tvPlaying, setTvPlaying] = useState(false);
  useEffect(() => {
    const handler = (e) => setTvPlaying(!!e.detail?.playing);
    document.addEventListener('game:watch_party_playing', handler);
    return () => document.removeEventListener('game:watch_party_playing', handler);
  }, []);
  // 🎬🍿 09-23 movie-night batch 1 (AUX_DUCK, MOVIE_NIGHT_UX_HANDOFF §3.6,
  // ruling 14): a SCREENING seating/playing in the house ducks the bed for
  // EVERYONE, seated or not (tvPlaying only ever fires for a seated, audible
  // viewer, so the un-seated crowd used to hear the bed under the film). The
  // page emits game:screening_live from the house payload. The credits leave
  // the room QUIET like a party's end: the fall sets tvHush explicitly below,
  // because the tvPlaying transition never saw these viewers play.
  const [screeningLive, setScreeningLive] = useState(false);
  const screeningLiveRef = useRef(false);
  const hushHoldUntilRef = useRef(0);
  useEffect(() => {
    const handler = (e) => setScreeningLive(!!e.detail?.live);
    document.addEventListener('game:screening_live', handler);
    return () => document.removeEventListener('game:screening_live', handler);
  }, []);

  // 🔇 Space master mute (Nina 08-02): the space's sound button silences the
  // radio too. Same contract as TV/case: radioActive (the user preference) is
  // untouched — the iframe unmounts and music returns on unmute. Leaving the
  // space (music url → null) clears the flag so the next space starts fresh
  // even though this FAB outlives page navigation.
  const [spaceAudioMuted, setSpaceAudioMuted] = useState(false);
  useEffect(() => {
    const muteHandler = (e) => setSpaceAudioMuted(!!e.detail?.muted);
    const urlHandler = (e) => { if (!e.detail?.musicUrl) setSpaceAudioMuted(false); };
    document.addEventListener('game:space_audio_muted', muteHandler);
    document.addEventListener('game:music_url_changed', urlHandler);
    return () => {
      document.removeEventListener('game:space_audio_muted', muteHandler);
      document.removeEventListener('game:music_url_changed', urlHandler);
    };
  }, []);

  // 🕵️ The case wins the room (Nina 07-25): during a live competitive
  // whodunnit the scenario ambience beds own the audio, and this FAB
  // (z 100010) was stacking over the investigation stage's timer/case-file
  // corner. Hide the whole radio; same contract as TV — radioActive is
  // untouched, everything returns when the case closes or you leave.
  const [caseActive, setCaseActive] = useState(false);
  useEffect(() => {
    const handler = (e) => setCaseActive(!!e.detail?.active);
    document.addEventListener('game:noir_case_active', handler);
    return () => document.removeEventListener('game:noir_case_active', handler);
  }, []);

  // 🎷 Stage 1 takeover (07-30): a scene-jazz pack keeps the radio MOUNTED
  // (unmount = audio dies), but the FAB was floating over the investigation
  // stage's timer/case-file corner (z100010 vs the stage's z1200). While the
  // takeover is up: FAB hidden, panel parked offscreen — the iframe keeps
  // playing, same contract as the minimized state. Everything returns when
  // the stage clears.
  const [stageUp, setStageUp] = useState(false);
  useEffect(() => {
    const handler = (e) => setStageUp(!!e.detail?.up);
    document.addEventListener('game:noir_stage_up', handler);
    return () => document.removeEventListener('game:noir_stage_up', handler);
  }, []);

  // 🏡⋯ A ported room's PLACE page (W2d, PORTED_HOME_HANDOFF §0-ter) keeps its
  // extras behind ONE ⋯ menu: the page hides this FAB (MUSIC_FAB_HIDDEN, and
  // un-hides it when it unmounts) and its "Music" item opens the panel
  // (MUSIC_OPEN). The FAB only: the panel opens where it always does and the
  // music never stops for it (unlike NOIR_STAGE_UP, which parks the panel).
  const [fabHidden, setFabHidden] = useState(false);
  const openFromMenuRef = useRef(null);
  useEffect(() => {
    const hide = (e) => setFabHidden(!!e.detail?.hidden);
    const open = () => { if (openFromMenuRef.current) openFromMenuRef.current(); };
    document.addEventListener('game:music_fab_hidden', hide);
    document.addEventListener('game:music_open', open);
    return () => {
      document.removeEventListener('game:music_fab_hidden', hide);
      document.removeEventListener('game:music_open', open);
    };
  }, []);

  // Resolve which URL to use: user override > space music > default
  const activeUrl = userMusicUrl || spaceMusicUrl;
  const parsed = parseYouTubeUrl(activeUrl);
  // Volume applies only while the SPACE's music is what's playing — a user
  // override track goes back to full volume.
  const effectiveVolume = (!userMusicUrl && spaceMusicUrl) ? spaceMusicVolume : null;
  // 🎵 J1 queue → video ids. Only while the SPACE's music plays (a user
  // override is theirs alone), and only plain video entries — a playlist-type
  // url already continues on its own.
  const queueIds = (!userMusicUrl && spaceMusicUrl && spaceMusicQueue)
    ? [...new Set(spaceMusicQueue
        .map((q) => parseYouTubeUrl(q?.url || ''))
        .filter((p) => p && p.type === 'video')
        .map((p) => p.id))]
    : null;
  // 🎲 Deck rotation (Nina 08-11: "the same song over and over is maddening").
  // The embed always led with spaceMusicUrl's track, so every time the radio
  // took the room (join, leave couch, TV handoff, master unmute) the iframe
  // remounted and the SAME head song restarted. Rotate the deck to a fresh
  // entry point each time the play gate OPENS — order stays the residents'
  // queue order, only where the needle drops changes. Ref-stable while
  // mounted so the URL doesn't churn remounts; single-track spaces have
  // nothing to rotate and build byte-identical URLs to before.
  // 🤫 TV-off hush (Nina 08-13, upgraded same-day from the single-track rule:
  // "keep the music off if a user pauses or shuts off the watch party").
  // Once the TV has AUDIBLY owned the room, handing it back — leave couch,
  // party end, pausing or muting the TV — leaves the bed QUIET. No
  // auto-resume, no restart. The ♫ FAB flips back to its invitation and a
  // tap brings music back (landing on the jukebox's shared needle when one
  // is live); a head-track change (new space, aux handover, personal
  // override, a first /jukebox record) also lifts it — fresh music is a
  // fresh ask. The walk-in rule is untouched: a muted ambient TV never sets
  // tvPlaying, so walk-in music still plays under it.
  const [tvHush, setTvHush] = useState(false);
  const prevTvForHushRef = useRef(false);
  useEffect(() => {
    if (prevTvForHushRef.current && !tvPlaying) setTvHush(true);
    prevTvForHushRef.current = tvPlaying;
  }, [tvPlaying]);
  // 🎬🍿 the credits: screening_live true → false hushes the bed for everyone
  // (the tap on ♫ is the ask, as after any party), and holds that hush through
  // the wrap's aux handover: the residents' rotation resumes at the wrap and
  // rewrites the head track, which would otherwise lift the hush mid-credits.
  useEffect(() => {
    if (screeningLiveRef.current && !screeningLive) {
      setTvHush(true);
      hushHoldUntilRef.current = Date.now() + 90 * 1000;
    }
    screeningLiveRef.current = screeningLive;
  }, [screeningLive]);
  const headId = parsed?.id || null;
  useEffect(() => {
    if (Date.now() < hushHoldUntilRef.current) return; // the wrap's handover, not a fresh ask
    setTvHush(false);
  }, [headId]);

  // Hush is part of the gate: rotation reseeds and the jukebox needle
  // re-freezes at the moment music actually CAN return (the ♫ tap), not at
  // the party's end while the room sits silent — a stale frozen offset would
  // land minutes behind the shared clock.
  const playGateOpen = radioActive && !tvPlaying && !spaceAudioMuted && !tvHush && !screeningLive;
  const deckRotationRef = useRef(0);
  const prevPlayGateRef = useRef(false);
  const gateJustOpened = playGateOpen && !prevPlayGateRef.current;
  if (gateJustOpened) {
    deckRotationRef.current = Math.floor(Math.random() * 997);
  }
  prevPlayGateRef.current = playGateOpen;
  // ⭐ A FRESH aux pick must lead, un-rotated: at every jukebox handover the
  // backend rewrites music_url to the incoming holder's NEW track and the
  // "aux passes to X · Now on: Y" card posts in chat — rotating that moment
  // would play some OTHER song under X's announcement. Head changed → the
  // new head leads (offset 0); rotation stays for the unchanged-head case
  // (leave couch, unmute), which is the maddening one.
  const lastHeadIdRef = useRef(null);
  const headChangedThisRender = (parsed?.id || null) !== lastHeadIdRef.current && lastHeadIdRef.current !== null;
  if ((parsed?.id || null) !== lastHeadIdRef.current) {
    if (lastHeadIdRef.current !== null && parsed?.id) deckRotationRef.current = 0;
    lastHeadIdRef.current = parsed?.id || null;
  }
  // 🎶 Who was LEADING last render — lets a clockless deck change (an /aux
  // add) keep its leader instead of re-rolling the rotation (fix a, 08-19).
  const prevLeaderIdRef = useRef(null);
  // 🎶 Was the current needle-freeze an ENTRY (gate open) or a deck change?
  // Entry keeps Nina's from-the-top rule; a mid-play add must NOT restart the
  // room's track (fix b, 08-19) — the backend rebases the anchor to preserve
  // the position, the old code discarded it via fromTop.
  const jbFreezeEntryRef = useRef(true);
  // 🎶 Jukebox shared clock (Nina 08-13): when the space carries an anchor
  // (jukebox_started_at) AND every deck track has a duration, the room's
  // needle position is pure arithmetic — elapsed since the anchor walked
  // across the durations, looping. The entry is FROZEN per freeze-event
  // (gate open / deck or anchor change), same idiom as deckRotationRef:
  // recomputing per render would churn embedUrl into a remount storm.
  // While active it REPLACES the random rotation; duration-less decks (the
  // house aux) and single tracks keep the existing paths untouched.
  const jukeboxDurations = {};
  if (spaceMusicQueue) {
    spaceMusicQueue.forEach((q) => {
      const p = parseYouTubeUrl(q?.url || '');
      if (p && p.type === 'video' && Number(q?.duration_s) > 0 && jukeboxDurations[p.id] == null) {
        jukeboxDurations[p.id] = Number(q.duration_s);
      }
    });
  }
  if (parsed && parsed.type === 'video' && Number(spaceJukebox?.head_duration_s) > 0 && jukeboxDurations[parsed.id] == null) {
    jukeboxDurations[parsed.id] = Number(spaceJukebox.head_duration_s);
  }
  const jbEntryRef = useRef(null);
  const prevJbSigRef = useRef('');
  let deckParsed = parsed;
  let deckIds = queueIds;
  // 🅰️/🅱️ (Nina 08-18): >30 min = ambience — join at the communal moment.
  // A normal-length song in a short (<3) deck starts from its top instead:
  // arrival should feel like the record dropping, not walking in mid-verse.
  const LONG_TRACK_S = 30 * 60;
  if (queueIds && queueIds.length && parsed && parsed.type === 'video') {
    const deck = [parsed.id, ...queueIds.filter((id) => id !== parsed.id)];
    // A lone LONG track still walks the shared clock (communal ambience);
    // lone normal songs keep the untouched single path (top, once, quiet).
    const singleLong = deck.length === 1 && Number(jukeboxDurations[deck[0]]) > LONG_TRACK_S;
    if (deck.length > 1 || singleLong) {
      // Anchor + deck identity: any change re-freezes the needle (a /jukebox
      // add rebases the anchor server-side, so adds land here too).
      const jbSig = `${spaceJukebox?.started_at || ''}|${deck.join(',')}`;
      if (spaceJukebox?.started_at && (gateJustOpened || jbSig !== prevJbSigRef.current)) {
        jbFreezeEntryRef.current = gateJustOpened;
        jbEntryRef.current = (() => {
          const startedMs = new Date(spaceJukebox.started_at).getTime();
          if (!Number.isFinite(startedMs)) return null;
          if (deck.some((id) => jukeboxDurations[id] == null)) return null;
          const total = deck.reduce((s, id) => s + jukeboxDurations[id], 0);
          if (!(total > 0)) return null;
          let elapsed = Math.max(0, (Date.now() - startedMs) / 1000) % total;
          for (const id of deck) {
            if (elapsed < jukeboxDurations[id]) return { id, offset: Math.floor(elapsed) };
            elapsed -= jukeboxDurations[id];
          }
          return { id: deck[0], offset: 0 };
        })();
      } else if (!spaceJukebox?.started_at) {
        jbEntryRef.current = null;
      }
      prevJbSigRef.current = jbSig;
      const jb = jbEntryRef.current;
      if (jb && deck.includes(jb.id)) {
        const at = deck.indexOf(jb.id);
        const spun = [...deck.slice(at), ...deck.slice(0, at)];
        // The shared offset outranks the head's own &t= — everyone lands
        // where the room is, mid-song included.
        const curDur = Number(jukeboxDurations[spun[0]]) || 0;
        // fromTop ONLY on entry: an add-remount keeps the rebased offset, so
        // the room's song continues where it was instead of restarting.
        const fromTop = jbFreezeEntryRef.current && deck.length < 3 && curDur > 0 && curDur <= LONG_TRACK_S;
        deckParsed = { ...parsed, id: spun[0], startSeconds: fromTop ? 0 : (jb.offset || 0) };
        deckIds = spun;
      } else {
        // 🎶 Fix (a), measured 08-19 (aux repro S2): when the clock can't
        // resolve (duration-less head, e.g. a livestream) an /aux add used to
        // re-roll the rotation — ~50% chance the JUST-ADDED song hijacked the
        // room. Mid-play deck changes now pin the previous leader; the random
        // seed still gives entry variety, and an aux HANDOVER (head change)
        // keeps its fresh-pick-leads rule via the reset above.
        if (!gateJustOpened && !headChangedThisRender
            && prevLeaderIdRef.current && deck.includes(prevLeaderIdRef.current)) {
          deckRotationRef.current = deck.indexOf(prevLeaderIdRef.current);
        }
        const off = deckRotationRef.current % deck.length;
        const spun = [...deck.slice(off), ...deck.slice(0, off)];
        // A &t= timestamp belongs to the ORIGINAL head track only — a rotated
        // leader starts from its own top.
        deckParsed = { ...parsed, id: spun[0], startSeconds: spun[0] === parsed.id ? parsed.startSeconds : 0 };
        deckIds = spun;
      }
    }
  }
  prevLeaderIdRef.current = (deckIds && deckIds[0]) || parsed?.id || null;
  const embedUrl = buildEmbedUrl(deckParsed, effectiveVolume != null, deckIds);
  const isCustomMusic = !!parsed;
  // 🎶 Up-next rail (rulings 08-15, built 08-18): the room's ACTUAL play
  // order — fed the resolved deck (deckIds, already spun by the shared clock
  // or rotation), never the raw queue, which lies whenever the clock can't
  // resolve. Hidden under a personal override (that track is theirs alone)
  // and for playlist-type urls (YouTube owns their order).
  const railEntries = (!userMusicUrl && spaceMusicUrl && parsed && parsed.type === 'video')
    ? buildRailEntries(deckIds || [parsed.id], spaceMusicQueue, parsed.id, spaceHeadTitle)
    : [];
  const railVisible = railEntries.length > 0
    && !!(railEntries[0].title || railEntries.slice(1).some((e) => e.title));

  // 🕰️ Live needle (08-20 rail hybrid): the room's nominal position from the
  // shared clock — powers the progress bar AND keeps the rail's order honest
  // after the embed advances (the static spun order went stale one track in).
  // Nominal, not a player read: buffering can drift a viewer a few seconds.
  // Null whenever the clock can't resolve → no bar, static order (today's
  // behavior, e.g. duration-less livestream heads).
  const [railNowMs, setRailNowMs] = useState(() => Date.now());
  const railClockLive = railVisible && !!spaceJukebox?.started_at && (deckIds || []).length > 0;
  useEffect(() => {
    if (!railClockLive) return undefined;
    const t = setInterval(() => setRailNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [railClockLive]);
  // ⚠️ The anchor is defined over the HEAD-FIRST order; deckIds is SPUN to
  // the entry leader — rebase before walking or every mid-deck entrant reads
  // one track ahead of the audio (F5, preship #92; parsed.id = the original
  // head by construction of the deck).
  const railWalk = railClockLive
    ? resolveDeckPosition(rebaseDeckToHead(deckIds, parsed.id), jukeboxDurations, new Date(spaceJukebox.started_at).getTime(), railNowMs)
    : null;
  // Rotate the DISPLAY to the walked now-playing track; the embed advances on
  // its own, this keeps "▶" pointed at the truth without a remount.
  const railLive = (() => {
    if (!railWalk) return railEntries;
    const at = railEntries.findIndex((e) => e.id === railWalk.currentId);
    return at > 0 ? [...railEntries.slice(at), ...railEntries.slice(0, at)] : railEntries;
  })();

  // 🖼️ Credit dot (08-20: Nina's pick — avatar dots instead of credit words).
  // Avatar when the entry stored one, initials circle otherwise, nothing when
  // uncredited (builder heads). Decorative: the name rides the tooltip.
  const CreditDot = ({ entry, size }) => {
    if (!entry.credit) return null;
    const st = {
      width: size, height: size, minWidth: size, borderRadius: '50%',
      marginRight: 5, flexShrink: 0, boxSizing: 'border-box',
    };
    if (entry.by_avatar) {
      return <img src={entry.by_avatar} alt={entry.credit} title={`Added by ${entry.credit}`}
        style={{ ...st, objectFit: 'cover', display: 'block' }} />;
    }
    const hue = [...String(entry.credit)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
    return <span title={`Added by ${entry.credit}`} style={{
      ...st, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: `hsl(${hue}, 45%, 38%)`, color: 'rgba(255,255,255,0.9)',
      fontSize: size * 0.55, fontWeight: 700, lineHeight: 1,
    }}>{String(entry.credit).trim()[0]?.toUpperCase() || '♪'}</span>;
  };


  // Track whether embed URL changed (to force iframe remount via key)
  const embedUrlChanged = prevEmbedUrlRef.current !== null && prevEmbedUrlRef.current !== embedUrl;
  prevEmbedUrlRef.current = embedUrl;

  // 🎚️ Apply the requested volume via the YT iframe API (postMessage). The
  // widget takes a beat to boot and there is no ready callback without the
  // full API script, so re-send over the first seconds — the early sends are
  // dropped harmlessly, the first one after boot lands. Re-runs on remount
  // (embedUrl key) and whenever the requested volume changes.
  const playerIframeRef = useRef(null);
  useEffect(() => {
    if (effectiveVolume == null || !radioActive) return undefined;
    const send = () => {
      try {
        playerIframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'setVolume', args: [effectiveVolume] }), '*');
      } catch (e) { /* iframe not mounted yet */ }
    };
    const timers = [50, 500, 1200, 2500, 4500].map((ms) => setTimeout(send, ms));
    return () => timers.forEach(clearTimeout);
  }, [effectiveVolume, radioActive, embedUrl]);

  // Track playback start time (reset when embed URL changes or radio starts)
  useEffect(() => {
    if (radioActive) {
      playStartedAtRef.current = Date.now();
    } else {
      playStartedAtRef.current = null;
    }
  }, [radioActive, embedUrl]);

  // 🎵 Builder music auto-starts (D6, Nina 07-12): a space's OWN soundtrack
  // begins the moment you arrive — through loading and the welcome
  // interstitials — because the builder explicitly chose it. The DEFAULT lofi
  // stream still never auto-starts (that removal stands). If no gesture has
  // happened yet (hard load), the first tap/keypress anywhere starts it.
  // Stopping it is respected for the rest of the session.
  const autoPlayingRef = useRef(false);   // current playback was auto-started
  const autoStartedUrlRef = useRef(null); // one shot per space URL
  const userStoppedRef = useRef(false);   // user said no — stay quiet this session
  useEffect(() => {
    if (!spaceMusicUrl || radioActive || userStoppedRef.current) return;
    if (autoStartedUrlRef.current === spaceMusicUrl) return;
    const start = () => {
      autoStartedUrlRef.current = spaceMusicUrl;
      autoPlayingRef.current = true;
      setRadioActive(true); // player stays minimized — just the button lights up
    };
    if (audioGestureSeen) {
      start();
      return;
    }
    const onGesture = () => start();
    document.addEventListener('pointerdown', onGesture, { capture: true, once: true });
    document.addEventListener('keydown', onGesture, { capture: true, once: true });
    return () => {
      document.removeEventListener('pointerdown', onGesture, { capture: true });
      document.removeEventListener('keydown', onGesture, { capture: true });
    };
  }, [spaceMusicUrl, radioActive]);

  // Leaving the space (music URL cleared): auto-started music stops rather
  // than falling through to the default lofi stream — auto-play is only ever
  // the builder's soundtrack, never the generic radio.
  useEffect(() => {
    if (!spaceMusicUrl && autoPlayingRef.current && !userMusicUrl) {
      autoPlayingRef.current = false;
      setRadioActive(false);
      setShowPlayer(false);
    }
  }, [spaceMusicUrl, userMusicUrl]);

  const handleClick = () => {
    if (tvHush) {
      // Hushed after a party in a thin-deck space — the tap IS the ask
      setTvHush(false);
      setShowPlayer(true);
      return;
    }
    if (!radioActive) {
      autoPlayingRef.current = false; // user-started: theirs now
      userStoppedRef.current = false;
      setRadioActive(true);
      setShowPlayer(true);
    } else if (showPlayer) {
      setShowPlayer(false);
      setShowEdit(false);
    } else {
      setShowPlayer(true);
    }
  };

  // the menu's "Music": the same as a tap that OPENS (never a tap that minimizes)
  openFromMenuRef.current = () => {
    if (tvHush) { setTvHush(false); setShowPlayer(true); return; }
    if (!radioActive) {
      autoPlayingRef.current = false;
      userStoppedRef.current = false;
      setRadioActive(true);
    }
    setShowPlayer(true);
  };

  const handleStop = () => {
    // Explicit stop wins for the whole session — no re-auto-start
    userStoppedRef.current = true;
    autoPlayingRef.current = false;
    setRadioActive(false);
    setShowPlayer(false);
    setShowEdit(false);
  };

  const handleEditSubmit = () => {
    const trimmed = editInput.trim();
    if (!trimmed) return;
    const validParsed = parseYouTubeUrl(trimmed);
    if (!validParsed) return; // Invalid URL — do nothing
    setUserMusicUrl(trimmed);
    setShowEdit(false);
    autoPlayingRef.current = false; // user picked their own track: theirs now
    // If radio is off, start it
    if (!radioActive) {
      userStoppedRef.current = false;
      setRadioActive(true);
      setShowPlayer(true);
    }
  };

  const handleEditReset = () => {
    setUserMusicUrl(null);
    setEditInput('');
    setShowEdit(false);
  };

  // Mobile: smaller + tucked tighter into the corner — the 40px FAB at 14/14
  // sat on page titles and card content everywhere (mobile audit, Nina 07-17)
  const btnSize = isMobile ? 34 : 48;
  const rightPos = (isGamePage && !isMobile) ? 200 : (isMobile ? 10 : 20);
  const topPos = (isGamePage && !isMobile) ? 10 : (isMobile ? 10 : 20);

  const label = isCustomMusic ? 'Space Music' : 'Lofi Radio';
  const playerWidth = isMobile ? 200 : 220;

  // This space has its own soundtrack and it isn't playing (user stopped it,
  // the start is still gesture-locked, or the post-party hush) — wiggle the
  // button as an invitation. fabPlaying = what the button should LOOK like:
  // hushed reads as "not playing" so the tap-to-play affordance returns.
  const fabPlaying = radioActive && !tvHush && !screeningLive;
  const hasSpecialMusic = isCustomMusic && !fabPlaying;

  if (caseActive) return null;

  return (
    <>
      {hasSpecialMusic && (
        <style>{`
          @keyframes lofiWiggle {
            0%, 70%, 100% { transform: rotate(0deg) scale(1); }
            74% { transform: rotate(-12deg) scale(1.08); }
            78% { transform: rotate(10deg) scale(1.08); }
            82% { transform: rotate(-8deg) scale(1.06); }
            86% { transform: rotate(6deg) scale(1.04); }
            90% { transform: rotate(0deg) scale(1); }
          }
          @keyframes lofiNoteGlow {
            0%, 100% { box-shadow: 0 2px 10px rgba(0,0,0,0.15), 0 0 0 0 rgba(123,31,162,0.4); }
            50% { box-shadow: 0 2px 10px rgba(0,0,0,0.15), 0 0 0 7px rgba(123,31,162,0); }
          }
        `}</style>
      )}
      {/* Music button */}
      <button
        onClick={handleClick}
        style={{
          position: 'fixed',
          top: topPos,
          right: rightPos,
          width: btnSize,
          height: btnSize,
          borderRadius: '50%',
          backgroundColor: fabPlaying ? '#fff' : '#7b1fa2',
          color: fabPlaying ? '#7b1fa2' : '#fff',
          border: '2px solid #7b1fa2',
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          cursor: 'pointer',
          display: (stageUp || fabHidden) ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          // Rest a little transparent on phones so it reads as chrome, not
          // content, when it floats over busy headers (07-17)
          opacity: isMobile && !fabPlaying ? 0.82 : 1,
          zIndex: 100010,
          transition: 'all 0.2s',
          animation: hasSpecialMusic ? 'lofiWiggle 3.2s ease-in-out infinite, lofiNoteGlow 3.2s ease-in-out infinite' : 'none',
        }}
        title={screeningLive
          ? 'The projector has the room tonight.'
          : !fabPlaying
          ? (isCustomMusic ? 'This space has its own soundtrack — tap to play!' : `Listen to ${label}`)
          : showPlayer ? `Minimize ${label}` : `Show ${label}`}
      >
        ♫
        {hasSpecialMusic && (
          <span style={{
            position: 'absolute', top: -4, right: -4, fontSize: '0.6em',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
          }}>✨</span>
        )}
      </button>

      {/* Iframe stays in DOM while active — hidden offscreen when minimized so music keeps playing.
          Unmounted while the space TV / a watch party plays (tvPlaying) so audio never stacks,
          and through the post-TV hush (tvHush — the bed stays off until asked). */}
      {playGateOpen && (
        <div style={{
          position: 'fixed',
          top: (showPlayer && !stageUp) ? topPos : -9999,
          right: (showPlayer && !stageUp) ? (rightPos + btnSize + 10) : -9999,
          width: playerWidth,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: showPlayer ? '0 4px 20px rgba(0,0,0,0.2)' : 'none',
          zIndex: 100009,
        }}>
          {/* Minimize icon at top-right corner */}
          {showPlayer && (
            <button
              onClick={() => { setShowPlayer(false); setShowEdit(false); }}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                lineHeight: 1,
              }}
              title="Minimize"
            >
              ▾
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <iframe
              key={embedUrl}
              ref={playerIframeRef}
              src={embedUrl}
              title={label}
              style={{ width: '100%', height: 124, border: 'none' }}
              allow="autoplay; encrypted-media"
              allowFullScreen
              frameBorder="0"
            />
            {/* Drag handle */}
            <div
              draggable="true"
              onDragStart={(e) => {
                const elapsedSec = playStartedAtRef.current
                  ? Math.floor((Date.now() - playStartedAtRef.current) / 1000)
                  : 0;
                const baseUrl = embedUrl.replace(/[?&]autoplay=1/, '');
                const shareUrl = elapsedSec > 0 ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}start=${elapsedSec}` : baseUrl;
                e.dataTransfer.setData('text/x-youtube-url', shareUrl);
                e.dataTransfer.setData('text/plain', shareUrl);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{
                padding: '4px 0',
                textAlign: 'center',
                fontSize: '0.65rem',
                color: 'rgba(255,255,255,0.7)',
                backgroundColor: 'rgba(0,0,0,0.4)',
                cursor: 'grab',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              ⠿ Drag to chat
            </div>
          </div>

          {/* 🎶 Up-next rail. House framing = THE AUX: name the holder,
              credit whose turn is next (Nina: not a queue the visitor
              controls). ⚠️ No global border-box in this app — width:100% +
              padding needs explicit boxSizing or the rail overflows the
              200px panel. */}
          {showPlayer && railVisible && (
            <div style={{
              width: '100%', boxSizing: 'border-box', padding: '6px 8px',
              backgroundColor: '#141428', maxHeight: 118, overflowY: 'auto',
            }}>
              {spaceAuxHolder && (
                <div style={{
                  fontSize: '0.62rem', fontWeight: 700, color: '#ce93d8', marginBottom: 3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  🎙️ {spaceAuxHolder} has the aux
                </div>
              )}
              {/* 🎛️ 08-20 hybrid (Nina: A base + B's bar/artist + avatar
                  dots for credit words). Now playing: dot · title / artist
                  under / progress bar from the shared clock (railWalk null →
                  no bar, static order — clockless decks look like before). */}
              {railLive[0].title && (
                <div style={{ marginBottom: railWalk ? 1 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <CreditDot entry={railLive[0]} size={16} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: '0.64rem', color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        ▶ {railLive[0].title}
                      </div>
                      {railLive[0].author && (
                        <div style={{
                          fontSize: '0.56rem', color: 'rgba(255,255,255,0.45)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {railLive[0].author}
                        </div>
                      )}
                    </div>
                  </div>
                  {railWalk && railWalk.currentId === railLive[0].id && (
                    <div style={{
                      marginTop: 3, height: 3, borderRadius: 2, overflow: 'hidden',
                      backgroundColor: 'rgba(255,255,255,0.12)', boxSizing: 'border-box',
                    }}>
                      <div style={{
                        height: '100%', borderRadius: 2, backgroundColor: '#ce93d8',
                        width: `${Math.min(100, Math.max(0, railWalk.fraction * 100))}%`,
                        transition: 'width 1s linear',
                      }} />
                    </div>
                  )}
                </div>
              )}
              {railLive.slice(1).filter((e) => e.title).slice(0, 4).map((e) => (
                <div key={e.id} style={{
                  display: 'flex', alignItems: 'center', minWidth: 0,
                  fontSize: '0.62rem', color: 'rgba(255,255,255,0.65)', marginTop: 2,
                }}>
                  <span style={{ marginRight: 3 }}>↳</span>
                  <CreditDot entry={e} size={12} />
                  <span style={{
                    minWidth: 0, flex: 1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {e.title}
                    {formatTrackDuration(e.duration_s) ? <span style={{ color: 'rgba(255,255,255,0.35)' }}> {formatTrackDuration(e.duration_s)}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 💡 Short-deck tip (Nina 08-18): under three tracks the room plays
              through and goes quiet — teach the fix exactly where the music
              lives. Hidden in competitive spaces (commands are non-comp only)
              and under a personal override. */}
          {showPlayer && !userMusicUrl && spaceMusicUrl && !spaceCompetitive
            && parsed && parsed.type === 'video'
            && ((deckIds && deckIds.length) || 1) < 3 && (
            <div style={{
              width: '100%', boxSizing: 'border-box', padding: '5px 8px',
              backgroundColor: '#141428', fontSize: '0.6rem',
              color: 'rgba(255,255,255,0.45)',
            }}>
              💡 Deck's short. Add a song: /aux song name
            </div>
          )}

          {/* Action buttons row: Stop + Edit */}
          {showPlayer && (
            <div style={{ display: 'flex' }}>
              <button
                onClick={handleStop}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  background: '#7b1fa2',
                  color: '#fff',
                  border: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {`Stop ${label}`}
              </button>
              <button
                onClick={() => {
                  setShowEdit(!showEdit);
                  if (!showEdit) {
                    setEditInput(userMusicUrl || spaceMusicUrl || '');
                  }
                }}
                style={{
                  width: 36,
                  padding: '6px 0',
                  background: showEdit ? '#5c1580' : '#9c27b0',
                  color: '#fff',
                  border: 'none',
                  borderLeft: '1px solid rgba(255,255,255,0.2)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
                title="Change music URL"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          )}

          {/* Edit URL input panel */}
          {showPlayer && showEdit && (
            <div style={{
              padding: '8px',
              backgroundColor: '#1a1a2e',
            }}>
              <input
                type="text"
                value={editInput}
                onChange={(e) => setEditInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleEditSubmit(); }}
                placeholder="Paste YouTube URL..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: '0.7rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                <button
                  onClick={handleEditSubmit}
                  disabled={!editInput.trim() || !parseYouTubeUrl(editInput.trim())}
                  style={{
                    flex: 1,
                    padding: '5px 0',
                    background: editInput.trim() && parseYouTubeUrl(editInput.trim()) ? '#7b1fa2' : '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: editInput.trim() && parseYouTubeUrl(editInput.trim()) ? 'pointer' : 'default',
                    opacity: editInput.trim() && parseYouTubeUrl(editInput.trim()) ? 1 : 0.5,
                  }}
                >
                  Play
                </button>
                {userMusicUrl && (
                  <button
                    onClick={handleEditReset}
                    style={{
                      padding: '5px 10px',
                      background: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 4,
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                    }}
                    title="Reset to default"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LofiRadio;
